import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Phone, Mail, CheckCircle, XCircle, Filter, Zap } from "lucide-react";
import { format } from "date-fns";
import BookingFilters, { BookingFilters as BookingFiltersType, BookingFilterOptions } from "@/components/ui/BookingFilters";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/lib/currency";
import { useI18n } from "@/i18n/I18nProvider";

interface BookingInboxProps {
  bookings: any[];
  onAccept: (bookingId: string) => void;
  onDecline: (bookingId: string) => void;
  onOpenOps?: (booking: any) => void;
}

type InboxView = "action" | "urgent" | "confirmed" | "all";

function ModeBadge({ mode }: { mode: string | null | undefined }) {
  const { t } = useI18n();
  switch (mode) {
    case "instant":
      return <Badge className="bg-sky-100 text-sky-900 shrink-0">{t("vendor.inbox.immediate")}</Badge>;
    case "request":
      return <Badge className="bg-amber-100 text-amber-900 shrink-0">{t("vendor.inbox.request")}</Badge>;
    case "urgent":
      return (
        <Badge className="bg-orange-100 text-orange-900 shrink-0 gap-1">
          <Zap className="h-3 w-3" /> {t("vendor.inbox.urgent_badge")}
        </Badge>
      );
    default:
      return null;
  }
}

function SlaHint({ at }: { at?: string | null }) {
  const { t, tx } = useI18n();
  if (!at) return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  const left = d.getTime() - Date.now();
  if (left <= 0) {
    return <span className="text-xs text-destructive">{t("vendor.inbox.sla_expired")}</span>;
  }
  const mins = Math.floor(left / 60000);
  return (
    <span className="text-xs text-muted-foreground">
      {tx("vendor.inbox.sla_vendor_response", { mins, deadline: format(d, "PPp") })}
    </span>
  );
}

function needsVendorAction(b: any): boolean {
  return (
    b.status === "pending" &&
    (b.approval_status === "pending_vendor" ||
      (b.approval_status == null && b.booking_mode == null))
  );
}

export default function BookingInbox({ bookings, onAccept, onDecline, onOpenOps }: BookingInboxProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [filters, setFilters] = useState<BookingFiltersType>({
    search: "",
    sortBy: "date",
    sortOrder: "desc",
  });
  const [filterOptions, setFilterOptions] = useState<BookingFilterOptions>({
    customers: [],
    services: [],
  });
  const [inboxView, setInboxView] = useState<InboxView>("action");

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customers = Array.from(new Set(bookings.map((b) => b.customer_id).filter(Boolean))).map((id) => {
          const booking = bookings.find((b) => b.customer_id === id);
          return {
            id,
            name: booking?.customer_name || "Unknown Customer",
            email: booking?.customer_email || "",
          };
        });
        const services = Array.from(new Set(bookings.map((b) => b.service_id).filter(Boolean))).map((id) => {
          const booking = bookings.find((b) => b.service_id === id);
          return {
            id,
            name: booking?.service_name || "Unknown Service",
          };
        });
        setFilterOptions({ customers, services });
      } catch (error) {
        console.error("Error loading filter options:", error);
      }
    };
    if (bookings.length > 0) loadFilterOptions();
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (booking) =>
          booking.booking_number?.toLowerCase().includes(searchLower) ||
          booking.customer_name?.toLowerCase().includes(searchLower) ||
          booking.customer_email?.toLowerCase().includes(searchLower) ||
          booking.service_name?.toLowerCase().includes(searchLower) ||
          booking.address?.toLowerCase().includes(searchLower)
      );
    }
    if (filters.customerId) {
      filtered = filtered.filter((booking) => booking.customer_id === filters.customerId);
    }
    if (filters.serviceId) {
      filtered = filtered.filter((booking) => booking.service_id === filters.serviceId);
    }
    if (filters.status) {
      filtered = filtered.filter((booking) => booking.status === filters.status);
    }
    if (filters.dateFrom || filters.dateTo) {
      // Compare as yyyy-MM-dd strings to avoid timezone drift.
      const from = filters.dateFrom ? filters.dateFrom.toISOString().slice(0, 10) : null;
      const to = filters.dateTo ? filters.dateTo.toISOString().slice(0, 10) : null;
      filtered = filtered.filter((booking) => {
        const d = String(booking.scheduled_date || "").slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case "date":
          comparison =
            new Date(`${a.scheduled_date}T${a.scheduled_time || "00:00"}`).getTime() -
            new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}`).getTime();
          break;
        case "amount":
          comparison = (a.total_amount || 0) - (b.total_amount || 0);
          break;
        case "created":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      // Stable tiebreaker so equal keys don't reorder between renders.
      if (comparison === 0) {
        comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
        if (comparison === 0) comparison = String(a.id).localeCompare(String(b.id));
      }
      return filters.sortOrder === "asc" ? comparison : -comparison;
    });
    return filtered;
  }, [bookings, filters]);

  const inboxRows = useMemo(() => {
    const base = filteredBookings;
    // Explicit filters take precedence over the inbox-view preset: if the vendor
    // set a status/search/date/customer/service filter, honor it across ALL
    // bookings instead of letting the "action" preset hide the results.
    const hasExplicitFilter = !!(
      filters.status || filters.search || filters.dateFrom || filters.dateTo ||
      filters.customerId || filters.serviceId
    );
    if (hasExplicitFilter) return base;
    switch (inboxView) {
      case "action":
        return base.filter((b) => needsVendorAction(b));
      case "urgent":
        return base.filter((b) => b.booking_mode === "urgent");
      case "confirmed":
        return base.filter((b) => b.status === "confirmed");
      default:
        return base;
    }
  }, [filteredBookings, inboxView, filters]);

  const statusBadgeClass = (status: string) =>
    status === "pending"
      ? "bg-yellow-100 text-yellow-800"
      : status === "confirmed"
        ? "bg-blue-100 text-blue-800"
        : status === "in_progress"
          ? "bg-green-100 text-green-800"
          : status === "completed"
            ? "bg-gray-100 text-gray-800"
            : "bg-red-100 text-red-800";

  const showAcceptDecline = (b: any) =>
    b.status === "pending" && (b.approval_status === "pending_vendor" || (b.approval_status == null && b.booking_mode == null));

  const viewLabel =
    inboxView === "action"
      ? t("vendor.inbox.filter_pending")
      : inboxView === "urgent"
        ? t("vendor.inbox.filter_urgent")
        : inboxView === "confirmed"
          ? t("vendor.inbox.filter_confirmed")
          : t("vendor.inbox.filter_all");

  return (
    <div className="space-y-6 min-w-0 overflow-x-hidden">
      <Card>
        <CardHeader className="break-words">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t("vendor.inbox.filter_title")}
          </CardTitle>
          <CardDescription>{t("vendor.inbox.filter_desc")}</CardDescription>
        </CardHeader>
        <CardContent className="max-w-full overflow-x-hidden">
          <BookingFilters
            filters={filters}
            onFiltersChange={setFilters}
            options={filterOptions}
            showCustomerFilter={true}
            showServiceFilter={true}
            showStatusFilter={true}
            showDateFilter={true}
            showSorting={true}
            className="min-w-0"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("vendor.inbox.bookings_title")}</CardTitle>
          <CardDescription>
            {t("vendor.inbox.view_label")} {viewLabel} ({inboxRows.length})
          </CardDescription>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              size="sm"
              variant={inboxView === "action" ? "default" : "outline"}
              onClick={() => setInboxView("action")}
            >
              {t("vendor.inbox.tab_pending_short")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={inboxView === "urgent" ? "default" : "outline"}
              onClick={() => setInboxView("urgent")}
            >
              {t("vendor.inbox.tab_urgent")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={inboxView === "confirmed" ? "default" : "outline"}
              onClick={() => setInboxView("confirmed")}
            >
              {t("vendor.inbox.tab_confirmed")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={inboxView === "all" ? "default" : "outline"}
              onClick={() => setInboxView("all")}
            >
              {t("vendor.inbox.tab_all")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="min-w-0 overflow-x-hidden">
          {inboxRows.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t("vendor.inbox.empty")}</div>
          ) : (
            <div className="space-y-3">
              {inboxRows.map((b) => (
                <div
                  key={b.id}
                  className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 p-4 border rounded-lg min-w-0"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={statusBadgeClass(String(b.status)) + " shrink-0"}>
                        {String(b.status).charAt(0).toUpperCase() + String(b.status).slice(1)}
                      </Badge>
                      <ModeBadge mode={b.booking_mode} />
                      {(b as any)?.batch_id && (
                        <Badge variant="outline" className="border-primary text-primary shrink-0">
                          BULK
                        </Badge>
                      )}
                      <span className="font-medium truncate min-w-0" title={b.booking_number}>
                        {b.booking_number}
                      </span>
                    </div>
                    <div className="text-sm font-semibold break-words">{b.service_name}</div>
                    {b.total_amount != null && (
                      <div className="text-sm text-gray-700">
                        {t("vendor.inbox.total")} <span className="font-medium">{formatCurrency(Number(b.total_amount))}</span>
                      </div>
                    )}
                    {(b.booking_mode === "request" || b.booking_mode === "urgent") && b.vendor_response_deadline_at && (
                      <div>
                        <SlaHint at={b.vendor_response_deadline_at} />
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1 min-w-0">
                        <Calendar className="h-3 w-3 shrink-0" />
                        <span className="truncate">{format(new Date(b.scheduled_date), "MMM d, yyyy")}</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-0">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span className="truncate">{b.scheduled_time}</span>
                      </div>
                      <div className="flex items-center gap-1 min-w-0">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="break-words">{b.address}</span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm min-w-0">
                      <div className="flex items-center gap-1 text-muted-foreground min-w-0">
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="truncate">{b.customer_phone}</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground min-w-0">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate" title={b.customer_email}>
                          {b.customer_email}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0 sm:flex-col sm:flex-nowrap">
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/booking/${b.id}`)}>
                      {t("vendor.inbox.details")}
                    </Button>
                    {showAcceptDecline(b) && (
                      <>
                        <Button size="sm" onClick={() => onAccept(b.id)}>
                          <CheckCircle className="h-4 w-4 mr-1" /> {t("vendor.inbox.accept")}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onDecline(b.id)}>
                          <XCircle className="h-4 w-4 mr-1" /> {t("vendor.inbox.reject")}
                        </Button>
                      </>
                    )}
                    {onOpenOps && (
                      <Button variant="outline" size="sm" onClick={() => onOpenOps(b)}>
                        Ops
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
